import type { SurfGraphicTemplate, SurfLayer, SurfStyle } from "@vida/surf-core";
import {
  buildGraphicRenderPlan,
  getLayerGraphic,
  graphicIconPath,
  setGraphicItem,
} from "@vida/surf-core";

type Props = {
  layer: SurfLayer;
  theme: SurfStyle;
  selected: boolean;
  onGraphicChange?: (graphic: ReturnType<typeof getLayerGraphic>) => void;
};

export function GraphicLayerPreview({ layer, theme, selected, onGraphicChange }: Props) {
  const data = getLayerGraphic(layer);
  const plan = buildGraphicRenderPlan(data, theme);

  const editText = (itemIndex: number, field: "label" | "sublabel", value: string) => {
    if (!onGraphicChange) return;
    const patch = field === "label" ? { label: value } : { sublabel: value };
    onGraphicChange(setGraphicItem(data, itemIndex, patch));
  };

  return (
    <div className="editor-layer-graphic">
      <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="editor-layer-graphic-svg" aria-hidden>
        {plan.rects.map((rect, i) => (
          <rect
            key={`gr-${i}`}
            x={rect.x}
            y={rect.y}
            width={rect.w}
            height={rect.h}
            rx={rect.rx ?? 0}
            fill={rect.fill}
          />
        ))}
        {plan.lines.map((line, i) => (
          <line
            key={`gl-${i}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={line.stroke}
            strokeWidth={0.004}
          />
        ))}
        {plan.circles.map((circle, i) => (
          <circle key={`gc-${i}`} cx={circle.cx} cy={circle.cy} r={circle.r} fill={circle.fill} />
        ))}
        {plan.icons.map((icon, i) => (
          <g key={`gi-${i}`} transform={`translate(${icon.cx - icon.r} ${icon.cy - icon.r}) scale(${icon.r / 12})`}>
            <path
              d={graphicIconPath(icon.icon)}
              fill="none"
              stroke={icon.fill}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        ))}
        {plan.texts.map((t, i) => (
          <g key={`gt-${i}`}>
            {selected && onGraphicChange ? (
              <>
                <foreignObject x={t.x} y={t.y} width={t.w} height={(t.size ?? 0.05) * 1.2}>
                  <div
                    className="graphic-inline-label"
                    contentEditable
                    suppressContentEditableWarning
                    style={{ color: t.fill, fontSize: `${(t.size ?? 0.05) * 100}%` }}
                    onBlur={(e) => editText(t.itemIndex, "label", e.currentTarget.textContent ?? "")}
                  >
                    {t.text}
                  </div>
                </foreignObject>
                {t.subtext !== undefined && (
                  <foreignObject
                    x={t.x}
                    y={t.y + (t.size ?? 0.05)}
                    width={t.w}
                    height={(t.subSize ?? 0.035) * 1.2}
                  >
                    <div
                      className="graphic-inline-sublabel"
                      contentEditable
                      suppressContentEditableWarning
                      style={{ color: t.subFill ?? t.fill, fontSize: `${(t.subSize ?? 0.035) * 100}%` }}
                      onBlur={(e) => editText(t.itemIndex, "sublabel", e.currentTarget.textContent ?? "")}
                    >
                      {t.subtext}
                    </div>
                  </foreignObject>
                )}
              </>
            ) : (
              <>
                <text
                  x={t.anchor === "middle" ? t.x + t.w / 2 : t.x}
                  y={t.y + (t.size ?? 0.05)}
                  fill={t.fill}
                  fontSize={t.size ?? 0.05}
                  textAnchor={t.anchor ?? "start"}
                >
                  {t.text}
                </text>
                {t.subtext && (
                  <text
                    x={t.anchor === "middle" ? t.x + t.w / 2 : t.x}
                    y={t.y + (t.size ?? 0.05) + (t.subSize ?? 0.035)}
                    fill={t.subFill ?? t.fill}
                    fontSize={t.subSize ?? 0.035}
                    textAnchor={t.anchor ?? "start"}
                  >
                    {t.subtext}
                  </text>
                )}
              </>
            )}
          </g>
        ))}
      </svg>
      {!selected && (
        <span className="graphic-template-badge">{data.template as SurfGraphicTemplate}</span>
      )}
    </div>
  );
}
